'use client'

import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import type { MouseEvent } from 'react'
import { ArticleH2 } from '../ArticleHeading'
import { CARD_RADIUS } from '../cardRadius'

type InlineCTAComponentFields = {
  headerTabName?: unknown
  heading?: unknown
  description?: unknown
  ctaLabel?: unknown
  ctaHref?: unknown
  image?: unknown
  imageAlt?: unknown
}

export type InlineCTAProps = {
  component?: InlineCTAComponentFields
  heading?: string
  description?: string
  ctaLabel?: string
  ctaHref?: string
  /** Optional illustration shown on the right at md+, clipped by card overflow-hidden */
  imageSrc?: string
  imageAlt?: string
  className?: string
}

const asString = (value: unknown) => (typeof value === 'string' ? value : undefined)

const getMediaAttributes = (media: unknown) => {
  if (!media || typeof media !== 'object') return undefined

  const nestedAttributes = (media as { data?: { attributes?: unknown } }).data?.attributes

  if (nestedAttributes && typeof nestedAttributes === 'object') {
    return nestedAttributes as Record<string, unknown>
  }

  return media as Record<string, unknown>
}

const getMediaUrl = (media: unknown) => asString(getMediaAttributes(media)?.url)

const PillButton = ({
  label,
  href,
  onClick,
}: {
  label: string
  href: string
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void
}) => (
  <a
    href={href}
    onClick={onClick}
    className="self-start inline-flex items-center py-[12px] px-[20px] rounded-full [font-size:var(--font-body-md-size)] [font-weight:500] [line-height:1] no-underline shrink-0 transition-opacity hover:opacity-90"
    style={{
      backgroundColor: 'var(--color-primary)',
      color: 'var(--color-on-primary)',
    }}>
    {label}
  </a>
)

export const InlineCTA = ({
  component,
  heading,
  description,
  ctaLabel,
  ctaHref,
  imageSrc,
  imageAlt,
  className,
}: InlineCTAProps) => {
  const resolvedHeading = asString(component?.heading) ?? heading
  const resolvedDescription = asString(component?.description) ?? description
  const resolvedCtaLabel = asString(component?.ctaLabel) ?? ctaLabel
  const resolvedCtaHref = asString(component?.ctaHref) ?? ctaHref
  const resolvedImageSrc = getMediaUrl(component?.image) ?? imageSrc
  const resolvedImageAlt = asString(component?.imageAlt) ?? imageAlt ?? ''
  const cta = resolvedCtaLabel && resolvedCtaHref ? { label: resolvedCtaLabel, href: resolvedCtaHref } : undefined
  const handleCtaClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!cta) return

    event.preventDefault()
    void (async () => {
      await trackEvent('tap_btn_CTA_widget', {
        cta: cta.label,
        redirection_url: cta.href,
      })
      window.location.href = cta.href
    })()
  }

  if (!resolvedHeading) return null

  return (
    <div
      className={`@container relative overflow-hidden ${CARD_RADIUS} ${className ?? ''}`}
      style={{ backgroundColor: 'var(--color-primary-subtle)' }}>
      {/* Stretched link — covers the whole card on mobile; hidden at ≥540px where PillButton takes over */}
      {cta && (
        <a
          href={cta.href}
          aria-label={cta.label}
          onClick={handleCtaClick}
          className="@[540px]:hidden absolute inset-0 z-20"
        />
      )}

      {/* Narrow container (<540px): image stacks on top */}
      {resolvedImageSrc && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={resolvedImageSrc}
          alt={resolvedImageAlt}
          aria-hidden={resolvedImageAlt ? undefined : true}
          className="@[540px]:hidden w-full h-[220px] object-cover object-top block"
        />
      )}

      {/* Content — padded; on wide containers constrained so absolute image has room */}
      <div
        className={`relative z-10 p-[16px] @[540px]:p-[24px] flex flex-col gap-[24px]${resolvedImageSrc ? ' @[540px]:max-w-[480px]' : ''}`}>
        <div className="flex flex-col gap-[12px]">
          <ArticleH2>{resolvedHeading}</ArticleH2>
          {resolvedDescription && (
            <p
              className="[font-size:var(--font-body-md-size)] [line-height:var(--font-body-md-line)]"
              style={{ color: 'var(--color-text-secondary)' }}>
              {resolvedDescription}
            </p>
          )}
        </div>
        {/* PillButton only shown at ≥540px — on mobile the stretched link covers the card */}
        {cta && (
          <div className="hidden @[540px]:block">
            <PillButton label={cta.label} href={cta.href} onClick={handleCtaClick} />
          </div>
        )}
      </div>

      {/* Wide container (≥540px): absolute image on right, clips to card */}
      {resolvedImageSrc && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={resolvedImageSrc}
          alt={resolvedImageAlt}
          aria-hidden={resolvedImageAlt ? undefined : true}
          className="hidden @[540px]:block absolute right-0 top-0 h-full w-auto pointer-events-none object-cover"
        />
      )}
    </div>
  )
}
